import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateTimeOffQueries } from '@/modules/common/utils/query-invalidation';

import { updateTimeOffRequest } from '../api/time-offs.api';
import {
  extractTimeOffDayValidationErrors,
  TimeOffDayValidationError,
} from '../helpers/day-validation-errors.helper';
import { UpdateTimeOffRequestRequest } from '../types/dto/update-time-off-request.dto';

interface UseUpdateTimeOffRequestParams {
  onSuccess?: () => void;
  onDayValidationError?: (errors: TimeOffDayValidationError[]) => void;
}

export const useUpdateTimeOffRequest = ({
  onSuccess,
  onDayValidationError,
}: UseUpdateTimeOffRequestParams = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.timeOff');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: (payload: UpdateTimeOffRequestRequest) => updateTimeOffRequest(payload),
    onSuccess: async () => {
      await invalidateTimeOffQueries(queryClient);
      displayToastSuccess(t('toasts.updateSuccess'));
      onSuccess?.();
    },
    onError: error => {
      const dayErrors = extractTimeOffDayValidationErrors(error);
      if (dayErrors && dayErrors.length > 0) {
        onDayValidationError?.(dayErrors);
        return;
      }
      displayToastErrorFromApiWithFallback(error, 'toasts.updateError');
    },
  });
};
